import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ITransaction, studentService, TransactionItemMobile } from "../../../../entities/student";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import classes from './transactions.module.scss'
import { Calendar } from "../../../../shared/ui/calendar";
import { OpenContainer } from "../../../../features/openContainer";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { getDateUTC } from "../../../../shared/lib/helpers/getDateUTC";
import { Table } from "../table/Table";
import { AddManualTransaction } from "../../../../features/addManualTransaction";
import { useAppSelector } from "../../../../app/store/store";
import { DeleteAction } from "../../../../features/deleteAction";

interface IProps {
    studentId: number;
    targetUserId: number;
}

export const Transactions: FC<IProps> = ({studentId, targetUserId}) => {

    const [transactions, setTransactions] = useState<ITransaction[] | null>(null)
    const [count, setCount] = useState<number>(0)
    const [totalConfirmedAmount, setTotalConfirmedAmount] = useState<string>('0')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canManualCreate = hasFeature(my, FEATURES.TRANSACTIONS.STUDENT_MANUAL_CREATE, targetUserId)
    const canManualDelete = hasFeature(my, FEATURES.TRANSACTIONS.STUDENT_MANUAL_DELETE, targetUserId)
    const dateRangeRef = useRef<{from: string, to: string} | null>(null)

    const getTransactions = async (from: string, to: string) => {
        try{
            setIsLoading(true)
            const transactionsRes = await studentService.transactions(studentId, from, to)
            setTransactions(transactionsRes.transactions)
            setCount(transactionsRes.transactions_count)
            setTotalConfirmedAmount(transactionsRes.total_confirmed_amount)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении транзакций студента', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = useCallback((startDate: Date | null, endDate: Date | null) => {
        if(startDate && endDate){
            const from = getDateUTC(startDate)
            const to = getDateUTC(endDate)
            dateRangeRef.current = {from, to}
            getTransactions(from, to)
        }
    }, [])

    const onManualTransactionAdded = () => {
        if(dateRangeRef.current){
            getTransactions(dateRangeRef.current.from, dateRangeRef.current.to)
        }
    }

    const onManualTransactionDelete = async (transactionId: string) => {
        await studentService.deleteManualTransaction(studentId, transactionId)

        if(dateRangeRef.current){
            await getTransactions(dateRangeRef.current.from, dateRangeRef.current.to)
        }
    }

    const [trigger, setTrigger] = useState<number>(0)
    useEffect(() => {
        setTrigger(trigger + 1)
    }, [isLoading, transactions])

    return (
        <section className={classes.container}>
            <OpenContainer
                title="Транзакции"
                trigger={trigger}
                icon={<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7H18L15 4M20 17H6L9 20" /><path d="M18 7L20 7L20 12M6 17L4 17L4 12" /></svg>}
            >
                <section className={classes.content}>
                    <section className={classes.header}>
                        <Calendar onDateRangeSelect={setDate} />
                        {
                            canManualCreate
                                &&
                            <AddManualTransaction studentId={studentId} onSuccess={onManualTransactionAdded} />
                        }
                    </section>
                    {
                        isLoading
                            ?
                        <section className={classes.loader}><LoaderSpinner /></section>
                            :
                        transactions
                            &&
                        <>
                        {
                            !isLoading
                                &&
                            <>
                                <section className={classes.count}>Кол-во транзакций: {count}</section>
                                <section className={classes.count}>Сумма подтверждённых: {totalConfirmedAmount} ₽</section>
                            </>
                        }
                        {
                            transactions.length
                                ?
                            <>
                                <Table 
                                    transactions={transactions}
                                    canDelete={canManualDelete}
                                    onDelete={onManualTransactionDelete}
                                />
                                <section className={classes.items}>
                                    {transactions.map((transaction, ind) => 
                                        <TransactionItemMobile
                                            key={transaction.id} 
                                            transaction={transaction} 
                                        >
                                            {canManualDelete && transaction.can_delete &&
                                                <DeleteAction
                                                    questionText="Вы точно хотите удалить ручную транзакцию?"
                                                    errorText="Ошибка при удалении ручной транзакции"
                                                    successText="Ручная транзакция удалена"
                                                    onDelete={() => onManualTransactionDelete(transaction.id)}
                                                />
                                            }
                                        </TransactionItemMobile>
                                    )}
                                </section>
                            </>
                                :
                            <></>
                        }
                        </>
                    }
                </section>
            </OpenContainer>
        </section>
    )
}
